import React, { useState, useEffect } from 'react';
import {
  Search, MapPin, Calendar, User, Image as ImageIcon,
  CheckCircle, XCircle, Clock, Filter, ChevronRight,
  ExternalLink, Loader2, AlertCircle, Navigation,
  ThumbsUp, ThumbsDown, BadgeCheck, X, Eye, RefreshCw,
  AlertTriangle, Trash2, Database, Copy,
  Edit3, PlusCircle, Plus, Sparkles
} from 'lucide-react';
import { GoogleMap, useJsApiLoader, MarkerF } from '@react-google-maps/api';
import { useLocationSubmissions } from '../../authentication_components/LocationSubmissionContext';

const PUERTO_RICO_BOUNDS = {
  north: 18.52,
  south: 17.88,
  west: -67.95,
  east: -65.22
};

const mapContainerStyle = {
  width: '100%',
  height: '256px',
  borderRadius: '0.75rem'
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  mapTypeId: 'roadmap',
  gestureHandling: 'cooperative',
  styles: [
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] }
  ],
  restriction: {
    latLngBounds: PUERTO_RICO_BOUNDS,
    strictBounds: true
  }
};

// Standard location types — matches the prefix logic in getLocationIDPrefix on the backend
const LOCATION_TYPES = [
  { value: 'beach', label: 'Playa (Beach)' },
  { value: 'waterfall', label: 'Cascada (Waterfall)' },
  { value: 'mountain', label: 'Montaña / Pico' },
  { value: 'river', label: 'Río / Charco' },
  { value: 'laguna', label: 'Laguna' },
  { value: 'reserva', label: 'Reserva Natural' },
  { value: 'bridge', label: 'Puente' },
  { value: 'cavern', label: 'Cueva / Cavern' },
  { value: 'other', label: 'Otra' },
];

// =================================================================
// 1. Action Dialog (Approve/Reject/Duplicate) — unchanged
// =================================================================
const ActionDialog = ({ isOpen, onClose, type, onConfirm, loading }) => {
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const isApprove = type === 'approve';
  const isDuplicate = type === 'duplicate';

  const handleConfirm = () => {
    onConfirm(notes);
    setNotes('');
  };

  const getHeaderColor = () => {
    if (isApprove) return 'bg-emerald-50 border-emerald-100 text-emerald-800';
    if (isDuplicate) return 'bg-slate-50 border-slate-100 text-slate-800';
    return 'bg-red-50 border-red-100 text-red-800';
  };

  const getIcon = () => {
    if (isApprove) return <ThumbsUp className="h-6 w-6 text-emerald-600" />;
    if (isDuplicate) return <Copy className="h-6 w-6 text-slate-600" />;
    return <ThumbsDown className="h-6 w-6 text-red-600" />;
  };

  const getTitle = () => {
    if (isApprove) return 'Aprobar Ubicación';
    if (isDuplicate) return 'Marcar como Duplicada';
    return 'Rechazar Ubicación';
  };

  const getDescription = () => {
    if (isApprove) return 'Esta ubicación será visible públicamente en el mapa y se creará una entrada en la tabla de locaciones. ¿Desea agregar una nota administrativa?';
    if (isDuplicate) return 'Esta ubicación se marcará como duplicada y se notificará al usuario. Por favor indique cuál es la ubicación original o la razón.';
    return 'Por favor, indique la razón del rechazo. Esta nota será guardada para referencia.';
  };

  const getButtonClass = () => {
    if (isApprove) return 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200';
    if (isDuplicate) return 'bg-slate-600 hover:bg-slate-700 shadow-slate-200';
    return 'bg-red-600 hover:bg-red-700 shadow-red-200';
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        <div className={`p-6 border-b ${getHeaderColor()}`}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-white/50">{getIcon()}</div>
            <h3 className="text-lg font-bold">{getTitle()}</h3>
          </div>
        </div>

        <div className="p-6">
          <p className="text-slate-600 mb-4 text-sm">{getDescription()}</p>

          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Notas {isApprove ? '(Opcional)' : '(Recomendado)'}
          </label>
          <textarea
            className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-sm"
            rows={3}
            placeholder={isApprove ? "Ej. Coordenadas verificadas..." : "Ej. Ubicación ya existe, fotos borrosas..."}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`flex-1 px-4 py-2.5 text-white font-medium rounded-xl shadow-md transition-colors flex items-center justify-center disabled:opacity-50 ${getButtonClass()}`}
            >
              {loading ? <Loader2 className="animate-spin h-5 w-5" /> : 'Confirmar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 1.5 Delete Location Dialog — adapts to standalone vs coupled delete
// =================================================================
const DeleteLocationDialog = ({ isOpen, onClose, location, onConfirm, loading }) => {
  const [notes, setNotes] = useState('');
  const [confirmText, setConfirmText] = useState('');

  if (!isOpen) return null;

  const isConfirmValid = confirmText.toLowerCase() === 'eliminar';
  const hasSubmission = !!location?.submission_id;
  // Notes are only required when there's a submission to reject alongside the delete
  const isNotesValid = !hasSubmission || notes.trim().length > 0;

  const handleConfirm = () => {
    if (isConfirmValid && isNotesValid) {
      onConfirm(notes);
      setNotes('');
      setConfirmText('');
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6 border-b bg-red-50 border-red-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-red-100 text-red-600">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-bold text-red-800">
              {hasSubmission ? 'Eliminar Ubicación Aprobada' : 'Eliminar Ubicación'}
            </h3>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-amber-800 text-sm font-medium flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                {hasSubmission
                  ? 'Esta acción eliminará la ubicación de la base de datos y rechazará el envío asociado.'
                  : 'Esta acción eliminará permanentemente la ubicación de la base de datos.'}
                <strong className="block mt-1">Esta acción NO se puede deshacer.</strong>
              </span>
            </p>
          </div>

          {location && (
            <div className="bg-slate-50 p-3 rounded-lg mb-4 border border-slate-200">
              <p className="text-xs font-bold text-slate-500 uppercase mb-1">Ubicación a Eliminar</p>
              <p className="text-sm font-bold text-slate-800">{location.location_name}</p>
              <p className="text-xs text-slate-500 mt-1">
                Location ID: <span className="font-mono">{location.location_id}</span>
              </p>
              {hasSubmission ? (
                <p className="text-xs text-slate-500">
                  Submission ID: <span className="font-mono">{location.submission_id}</span>
                </p>
              ) : (
                <p className="text-xs text-violet-600 mt-1 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Ubicación creada por administrador (sin envío asociado)
                </p>
              )}
            </div>
          )}

          {hasSubmission && (
            <>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                Razón de Eliminación (Requerido)
              </label>
              <textarea
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm mb-4"
                rows={3}
                placeholder="Ej. Ubicación duplicada, información incorrecta..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </>
          )}

          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Confirmar Eliminación
          </label>
          <p className="text-xs text-slate-600 mb-2">
            Escriba <strong className="font-mono bg-slate-100 px-1 rounded">ELIMINAR</strong> para confirmar.
          </p>
          <input
            type="text"
            className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm font-mono"
            placeholder="ELIMINAR"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
          />

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || !isConfirmValid || !isNotesValid}
              className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-xl shadow-md shadow-red-200 hover:bg-red-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="animate-spin h-5 w-5" />
              ) : (
                <><Trash2 className="h-4 w-4 mr-2" /> Eliminar</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 1.75 Location Form Modal (Create + Edit)
// =================================================================
const LocationFormModal = ({ isOpen, mode, location, onClose, onSubmit, loading }) => {
  const isEdit = mode === 'edit';

  const [formData, setFormData] = useState({
    location_type: '',
    location_name: '',
    location_city: '',
    latitude: '',
    longitude: '',
    image_url: '',
  });
  const [validationError, setValidationError] = useState('');

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places']
  });

  // Pre-fill or reset on open
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && location) {
      setFormData({
        location_type: location.location_type || '',
        location_name: location.location_name || '',
        location_city: location.location_city || '',
        latitude: location.latitude != null ? String(location.latitude) : '',
        longitude: location.longitude != null ? String(location.longitude) : '',
        image_url: location.image_url || '',
      });
    } else {
      setFormData({
        location_type: '', location_name: '', location_city: '',
        latitude: '', longitude: '', image_url: '',
      });
    }
    setValidationError('');
  }, [isOpen, isEdit, location]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setFormData(prev => ({
      ...prev,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
    }));
  };

  const handleSubmit = () => {
    setValidationError('');

    if (!formData.location_type.trim()) {
      setValidationError('El tipo de ubicación es requerido');
      return;
    }
    if (!formData.location_name.trim()) {
      setValidationError('El nombre de la ubicación es requerido');
      return;
    }

    // Build payload — only send fields the user actually provided
    const payload = {
      location_type: formData.location_type,
      location_name: formData.location_name.trim(),
    };

    if (formData.location_city.trim()) {
      payload.location_city = formData.location_city.trim();
    }

    if (formData.latitude !== '' && !isNaN(parseFloat(formData.latitude))) {
      const lat = parseFloat(formData.latitude);
      if (lat < PUERTO_RICO_BOUNDS.south || lat > PUERTO_RICO_BOUNDS.north) {
        setValidationError(`La latitud debe estar dentro de Puerto Rico (${PUERTO_RICO_BOUNDS.south} - ${PUERTO_RICO_BOUNDS.north})`);
        return;
      }
      payload.latitude = lat;
    }

    if (formData.longitude !== '' && !isNaN(parseFloat(formData.longitude))) {
      const lng = parseFloat(formData.longitude);
      if (lng < PUERTO_RICO_BOUNDS.west || lng > PUERTO_RICO_BOUNDS.east) {
        setValidationError(`La longitud debe estar dentro de Puerto Rico (${PUERTO_RICO_BOUNDS.west} - ${PUERTO_RICO_BOUNDS.east})`);
        return;
      }
      payload.longitude = lng;
    }

    if (formData.image_url.trim()) {
      payload.image_url = formData.image_url.trim();
    }

    onSubmit(payload);
  };

  const hasCoords = formData.latitude !== '' && formData.longitude !== '' &&
    !isNaN(parseFloat(formData.latitude)) && !isNaN(parseFloat(formData.longitude));

  const mapCenter = hasCoords
    ? { lat: parseFloat(formData.latitude), lng: parseFloat(formData.longitude) }
    : { lat: 18.2208, lng: -66.5901 };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-6 border-b bg-violet-50 border-violet-100 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-white/70">
              {isEdit
                ? <Edit3 className="h-6 w-6 text-violet-600" />
                : <PlusCircle className="h-6 w-6 text-violet-600" />}
            </div>
            <div>
              <h3 className="text-lg font-bold text-violet-900">
                {isEdit ? 'Editar Ubicación' : 'Crear Nueva Ubicación'}
              </h3>
              <p className="text-xs text-violet-700 mt-0.5">
                {isEdit
                  ? 'Los créditos y el ID del envío original se preservan automáticamente.'
                  : 'Esta ubicación se creará con créditos "prlocal" y sin envío asociado.'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-white/50 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {/* Edit mode: read-only metadata */}
          {isEdit && location && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-slate-500 block mb-0.5">Location ID</span>
                <span className="font-mono font-semibold text-slate-700">{location.location_id}</span>
              </div>
              <div>
                <span className="text-slate-500 block mb-0.5">Créditos</span>
                <span className="font-semibold text-slate-700">{location.credentials || 'N/A'}</span>
              </div>
              {location.submission_id && (
                <div className="col-span-2">
                  <span className="text-slate-500 block mb-0.5">Submission ID (origen)</span>
                  <span className="font-mono font-semibold text-slate-700">{location.submission_id}</span>
                </div>
              )}
            </div>
          )}

          {validationError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{validationError}</p>
            </div>
          )}

          {/* Type */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
              Tipo de Ubicación <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-sm bg-white"
              value={formData.location_type}
              onChange={(e) => handleChange('location_type', e.target.value)}
              disabled={loading}
            >
              <option value="">-- Seleccionar tipo --</option>
              {LOCATION_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-sm"
              placeholder="Ej. Playa Flamenco"
              value={formData.location_name}
              onChange={(e) => handleChange('location_name', e.target.value)}
              disabled={loading}
            />
          </div>

          {/* City */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
              Ciudad / Pueblo
            </label>
            <input
              type="text"
              className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-sm"
              placeholder="Ej. Culebra"
              value={formData.location_city}
              onChange={(e) => handleChange('location_city', e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Coordinates */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
              Coordenadas
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Haz clic en el mapa para colocar el marcador, o ingresa los valores manualmente.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="number"
                step="any"
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-sm font-mono"
                placeholder="Latitud"
                value={formData.latitude}
                onChange={(e) => handleChange('latitude', e.target.value)}
                disabled={loading}
              />
              <input
                type="number"
                step="any"
                className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-sm font-mono"
                placeholder="Longitud"
                value={formData.longitude}
                onChange={(e) => handleChange('longitude', e.target.value)}
                disabled={loading}
              />
            </div>

            {isLoaded ? (
              <div className="border-2 border-slate-300 rounded-xl overflow-hidden shadow-sm">
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '300px' }}
                  center={mapCenter}
                  zoom={hasCoords ? 13 : 9}
                  onClick={handleMapClick}
                  options={{ ...mapOptions, gestureHandling: 'greedy' }}
                >
                  {hasCoords && <MarkerF position={mapCenter} />}
                </GoogleMap>
              </div>
            ) : (
              <div className="bg-slate-100 rounded-xl h-[300px] flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-violet-500 animate-spin" />
              </div>
            )}
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
              URL de Imagen
            </label>
            <input
              type="text"
              className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 outline-none text-sm"
              placeholder="https://..."
              value={formData.image_url}
              onChange={(e) => handleChange('image_url', e.target.value)}
              disabled={loading}
            />
            {formData.image_url && (
              <div className="mt-3 rounded-lg overflow-hidden border border-slate-200">
                <img
                  src={formData.image_url}
                  alt="Vista previa"
                  referrerPolicy="no-referrer"
                  className="w-full h-40 object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 bg-slate-50 flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-white transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-violet-600 text-white font-medium rounded-xl shadow-md shadow-violet-200 hover:bg-violet-700 transition-colors flex items-center justify-center disabled:opacity-50"
          >
            {loading
              ? <Loader2 className="animate-spin h-5 w-5" />
              : (isEdit ? 'Guardar Cambios' : 'Crear Ubicación')}
          </button>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 2. Submission Detail Modal — adds Edit button
// =================================================================
const SubmissionDetailModal = ({ submission: initialSubmission, onClose, onAction, onDelete, onEdit }) => {
  const { getSubmissionById } = useLocationSubmissions();
  const [fullSubmission, setFullSubmission] = useState(initialSubmission);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: ['places']
  });

  useEffect(() => {
    setFullSubmission(initialSubmission);
    if (!initialSubmission) return;

    const needsFetch =
      !initialSubmission.isFromLocationsTable &&
      (!initialSubmission.latitude || !initialSubmission.longitude);

    if (needsFetch) {
      const fetchDetails = async () => {
        setLoadingDetails(true);
        try {
          const details = await getSubmissionById(initialSubmission.submission_id);
          setFullSubmission(prev => ({ ...prev, ...details }));
        } catch (error) {
          console.error("Failed to fetch full submission details:", error);
        } finally {
          setLoadingDetails(false);
        }
      };
      fetchDetails();
    }
  }, [initialSubmission, getSubmissionById]);

  if (!fullSubmission) return null;

  const images = [
    fullSubmission.image_url_1 || fullSubmission.image_url,
    fullSubmission.image_url_2,
    fullSubmission.image_url_3
  ].filter(Boolean);

  const openInGoogleMaps = () => {
    if (fullSubmission.latitude && fullSubmission.longitude) {
      window.open(`https://www.google.com/maps?q=${fullSubmission.latitude},${fullSubmission.longitude}`, '_blank');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-PR', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const hasLocationId = fullSubmission.location_id &&
    (fullSubmission.submission_status === 'Approved' || fullSubmission.isFromLocationsTable);

  const mapCenter = fullSubmission.latitude && fullSubmission.longitude
    ? { lat: Number(fullSubmission.latitude), lng: Number(fullSubmission.longitude) }
    : { lat: 18.2208, lng: -66.5901 };

  const getStatusStyle = (status) => {
    if (status === 'Approved') return 'bg-emerald-50 border-emerald-100 text-emerald-800';
    if (status === 'Rejected') return 'bg-red-50 border-red-100 text-red-800';
    if (status === 'Duplicate') return 'bg-slate-100 border-slate-200 text-slate-800';
    return 'bg-amber-50 border-amber-100 text-amber-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'Approved') return <CheckCircle className="h-5 w-5" />;
    if (status === 'Rejected') return <XCircle className="h-5 w-5" />;
    if (status === 'Duplicate') return <Copy className="h-5 w-5" />;
    return <Clock className="h-5 w-5" />;
  };

  const getStatusText = (status) => {
    if (status === 'Approved') return 'Aprobado';
    if (status === 'Rejected') return 'Rechazado';
    if (status === 'Duplicate') return 'Duplicado';
    return 'Pendiente';
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 flex items-center justify-center p-4 md:p-8" onClick={onClose}>
      <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row relative" onClick={e => e.stopPropagation()}>

        {loadingDetails && (
          <div className="absolute inset-0 bg-white/60 z-50 flex items-center justify-center backdrop-blur-[1px]">
            <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-200 flex flex-col items-center">
              <Loader2 className="h-8 w-8 text-violet-600 animate-spin mb-2" />
              <span className="text-sm font-medium text-slate-600">Cargando coordenadas...</span>
            </div>
          </div>
        )}

        {/* Left Col: Images & Map */}
        <div className="md:w-1/2 bg-slate-100 p-6 overflow-y-auto border-r border-slate-200">
          <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-violet-500" /> Evidencia Visual ({images.length})
          </h4>

          {images.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 mb-6">
              {images.map((img, idx) => (
                <div key={idx} className="rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-white relative">
                  <img
                    src={img}
                    referrerPolicy="no-referrer"
                    alt={`Evidence ${idx + 1}`}
                    className="w-full h-64 object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+Error';
                    }}
                  />
                  <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                    Imagen {idx + 1}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-40 bg-slate-200 rounded-xl flex flex-col items-center justify-center text-slate-400 mb-6 border-2 border-dashed border-slate-300">
              <ImageIcon className="h-10 w-10 mb-2" />
              <span className="text-sm">Sin imágenes proporcionadas</span>
            </div>
          )}

          <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
            <Navigation className="h-5 w-5 text-violet-500" /> Ubicación Geográfica
          </h4>

          {loadError ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600">Error al cargar el mapa</p>
            </div>
          ) : !isLoaded || loadingDetails ? (
            <div className="bg-slate-200 rounded-xl h-64 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-violet-500 animate-spin" />
            </div>
          ) : fullSubmission.latitude && fullSubmission.longitude ? (
            <div className="space-y-3">
              <div className="border-2 border-slate-300 rounded-xl overflow-hidden shadow-md">
                <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={13} options={mapOptions}>
                  <MarkerF position={mapCenter} />
                </GoogleMap>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <p className="font-mono text-sm text-slate-600 font-semibold">
                  {Number(fullSubmission.latitude).toFixed(6)}, {Number(fullSubmission.longitude).toFixed(6)}
                </p>
                <p className="text-xs text-slate-500 mt-1">{fullSubmission.location_city}</p>
              </div>
              <button
                onClick={openInGoogleMaps}
                className="w-full px-4 py-2 bg-white border border-violet-200 rounded-lg text-sm font-bold text-violet-600 hover:bg-violet-50 transition-all flex items-center justify-center gap-2 shadow-sm"
              >
                <ExternalLink className="h-4 w-4" /> Abrir en Google Maps
              </button>
            </div>
          ) : (
            <div className="bg-slate-200 rounded-xl h-64 flex flex-col items-center justify-center relative overflow-hidden border border-slate-300">
              <MapPin className="h-10 w-10 text-slate-400 mb-2" />
              <p className="text-sm text-slate-500">Sin coordenadas disponibles</p>
            </div>
          )}
        </div>

        {/* Right Col: Details & Actions */}
        <div className="md:w-1/2 flex flex-col h-full">
          <div className="p-6 border-b border-slate-100 flex justify-between items-start bg-white">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs font-bold text-violet-500 bg-violet-50 px-2 py-0.5 rounded-full uppercase tracking-wide">
                  {fullSubmission.location_category || fullSubmission.location_type || 'Sin categoría'}
                </span>
                {fullSubmission.submission_id && (
                  <span className="text-xs text-slate-400 font-mono">{fullSubmission.submission_id}</span>
                )}
                {fullSubmission.isFromLocationsTable && (
                  <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Database className="h-3 w-3" /> En DB
                  </span>
                )}
              </div>
              <h2 className="text-2xl font-bold text-slate-800">{fullSubmission.location_name}</h2>
              <div className="flex items-center text-slate-500 text-sm mt-1">
                <MapPin className="h-3.5 w-3.5 mr-1" /> {fullSubmission.location_city || 'Sin ciudad'}
              </div>
              {hasLocationId && (
                <div className="mt-2 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md inline-flex items-center gap-1 font-mono">
                  <CheckCircle className="h-3 w-3" />
                  Location ID: {fullSubmission.location_id}
                </div>
              )}
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 flex-1 overflow-y-auto space-y-6">
            {!fullSubmission.isFromLocationsTable && fullSubmission.user_email && (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <h5 className="text-xs font-bold text-slate-400 uppercase mb-3">Enviado Por</h5>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-600">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-700">{fullSubmission.user_email}</span>
                      {fullSubmission.user_credentials && (
                        <span className="text-blue-500" title="Usuario Verificado">
                          <BadgeCheck className="h-4 w-4" />
                        </span>
                      )}
                    </div>
                    {fullSubmission.user_id && (
                      <p className="text-xs text-slate-500 font-mono">ID: {fullSubmission.user_id}</p>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-200 flex gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {formatDate(fullSubmission.submission_date)}
                  </span>
                  {fullSubmission.submission_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {fullSubmission.submission_time}
                    </span>
                  )}
                </div>
              </div>
            )}

            {fullSubmission.isFromLocationsTable && (
              <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                <h5 className="text-xs font-bold text-emerald-600 uppercase mb-3 flex items-center gap-1">
                  <Database className="h-3 w-3" /> Información de Base de Datos
                </h5>
                {fullSubmission.credentials && (
                  <div className="mb-2">
                    <span className="text-xs text-slate-500">Créditos:</span>
                    <p className="text-sm font-medium text-slate-700">{fullSubmission.credentials}</p>
                  </div>
                )}
                {fullSubmission.date_added && (
                  <div>
                    <span className="text-xs text-slate-500">Fecha Agregada:</span>
                    <p className="text-sm font-medium text-slate-700">{formatDate(fullSubmission.date_added)}</p>
                  </div>
                )}
                {!fullSubmission.submission_id && (
                  <div className="mt-2 pt-2 border-t border-emerald-200 text-xs text-violet-700 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> Creada por administrador
                  </div>
                )}
              </div>
            )}

            {fullSubmission.submission_notes && (
              <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
                <h5 className="text-xs font-bold text-amber-600 uppercase mb-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Notas del Usuario
                </h5>
                <p className="text-sm text-slate-700 italic">"{fullSubmission.submission_notes}"</p>
              </div>
            )}

            {fullSubmission.submission_status && fullSubmission.submission_status !== 'Pending' && (
              <div className={`p-4 rounded-xl border ${getStatusStyle(fullSubmission.submission_status)}`}>
                <h5 className="font-bold flex items-center gap-2">
                  {getStatusIcon(fullSubmission.submission_status)}
                  {getStatusText(fullSubmission.submission_status)}
                </h5>
              </div>
            )}

            {/* Edit & Delete buttons for any row that exists in prlocal_locations */}
            {hasLocationId && (
              <div className="space-y-3">
                <div className="bg-violet-50 p-4 rounded-xl border border-violet-200">
                  <h5 className="text-xs font-bold text-violet-700 uppercase mb-2 flex items-center gap-1">
                    <Edit3 className="h-3 w-3" /> Editar Ubicación
                  </h5>
                  <p className="text-sm text-violet-700 mb-3">
                    Actualiza nombre, coordenadas, imagen u otros datos. Los créditos y origen no se modifican.
                  </p>
                  <button
                    onClick={() => onEdit(fullSubmission)}
                    className="w-full py-2 px-3 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <Edit3 className="h-4 w-4" />
                    Editar Datos de la Ubicación
                  </button>
                </div>

                <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                  <h5 className="text-xs font-bold text-red-600 uppercase mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Zona de Eliminación
                  </h5>
                  <p className="text-sm text-red-700 mb-3">
                    {fullSubmission.submission_id
                      ? 'Esta ubicación está aprobada y visible públicamente. Eliminar también rechazará el envío original.'
                      : 'Esta ubicación fue creada por administrador. Eliminar la quita permanentemente del mapa.'}
                  </p>
                  <button
                    onClick={() => onDelete(fullSubmission)}
                    className="w-full py-2 px-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <Trash2 className="h-4 w-4" />
                    {fullSubmission.submission_id
                      ? 'Eliminar Ubicación y Rechazar Envío'
                      : 'Eliminar Ubicación'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {fullSubmission.submission_status === 'Pending' && !fullSubmission.isFromLocationsTable && (
            <div className="p-6 border-t border-slate-100 bg-slate-50 grid grid-cols-3 gap-3">
              <button
                onClick={() => onAction('reject', fullSubmission)}
                disabled={loadingDetails}
                className="py-3 px-2 bg-white border border-red-200 text-red-600 rounded-xl font-bold hover:bg-red-50 transition-colors flex flex-col items-center justify-center gap-1 text-sm disabled:opacity-50"
              >
                <XCircle className="h-5 w-5" /> Rechazar
              </button>
              <button
                onClick={() => onAction('duplicate', fullSubmission)}
                disabled={loadingDetails}
                className="py-3 px-2 bg-white border border-slate-300 text-slate-600 rounded-xl font-bold hover:bg-slate-100 transition-colors flex flex-col items-center justify-center gap-1 text-sm disabled:opacity-50"
              >
                <Copy className="h-5 w-5" /> Duplicado
              </button>
              <button
                onClick={() => onAction('approve', fullSubmission)}
                disabled={loadingDetails}
                className="py-3 px-2 bg-violet-600 text-white rounded-xl font-bold hover:bg-violet-700 shadow-lg shadow-violet-200 transition-colors flex flex-col items-center justify-center gap-1 text-sm disabled:opacity-50"
              >
                <CheckCircle className="h-5 w-5" /> Aprobar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =================================================================
// 3. Error Alert + 4. Success Toast — unchanged
// =================================================================
const ErrorAlert = ({ message, onDismiss }) => {
  if (!message) return null;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-bold text-red-800 text-sm">Error</h4>
          <p className="text-red-600 text-sm mt-1">{message}</p>
        </div>
        <button onClick={onDismiss} className="text-red-400 hover:text-red-600">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const SuccessToast = ({ message, onDismiss }) => {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => onDismiss(), 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onDismiss]);

  if (!message) return null;
  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3 shadow-lg">
        <CheckCircle className="h-5 w-5 text-emerald-600" />
        <p className="text-emerald-800 font-medium text-sm">{message}</p>
        <button onClick={onDismiss} className="text-emerald-400 hover:text-emerald-600">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

// =================================================================
// 5. MAIN COMPONENT
// =================================================================
export default function LocationSubmissions() {
  const {
    loading: contextLoading,
    error: contextError,
    getSubmissions,
    getPendingSubmissions,
    getApprovedSubmissions,
    getRejectedSubmissions,
    getDuplicateSubmissions,
    getAddedLocations,
    bulkGetLocationsBySubmissionIds,
    approveSubmission,
    rejectSubmission,
    markSubmissionAsDuplicate,
    deleteLocationAndReject,
    // NEW: direct location CRUD
    createLocation,
    updateLocation,
    deleteLocation,
    clearError,
  } = useLocationSubmissions();

  const [submissions, setSubmissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Pending');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [actionDialog, setActionDialog] = useState({ isOpen: false, type: null, submission: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, location: null });
  // NEW: form modal state for create/edit
  const [locationFormModal, setLocationFormModal] = useState({ isOpen: false, mode: 'create', location: null });
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    fetchSubmissions();
  }, [filterStatus]);

  const fetchSubmissions = async () => {
    try {
      setLocalError('');
      let result;

      switch (filterStatus) {
        case 'Pending':
          result = await getPendingSubmissions({ limit: 100 });
          setSubmissions(result.submissions || []);
          break;

        case 'Approved': {
          result = await getApprovedSubmissions({ limit: 100 });
          const approvedSubmissions = result.submissions || [];
          const submissionIds = approvedSubmissions.map(s => s.submission_id).filter(Boolean);

          if (submissionIds.length > 0) {
            try {
              const locationsResult = await bulkGetLocationsBySubmissionIds(submissionIds);
              const merged = approvedSubmissions.map(submission => {
                const matchingLocation = locationsResult.locations.find(
                  loc => loc.submission_id === submission.submission_id
                );
                return matchingLocation
                  ? { ...submission, ...matchingLocation, hasLocationData: true }
                  : submission;
              });
              setSubmissions(merged);
            } catch (locError) {
              console.warn('[Approved] Could not fetch location data:', locError);
              setSubmissions(approvedSubmissions);
            }
          } else {
            setSubmissions(approvedSubmissions);
          }
          break;
        }

        case 'Rejected':
          result = await getRejectedSubmissions({ limit: 100 });
          setSubmissions(result.submissions || []);
          break;

        case 'Duplicate':
          result = await getDuplicateSubmissions({ limit: 100 });
          setSubmissions(result.submissions || []);
          break;

        case 'AddedLocations': {
          result = await getAddedLocations({ limit: 100 });
          const locations = (result.locations || []).map(loc => ({ ...loc, isFromLocationsTable: true }));
          setSubmissions(locations);
          break;
        }

        case 'All':
          result = await getSubmissions({ limit: 100 });
          setSubmissions(result.submissions || []);
          break;

        default:
          result = await getPendingSubmissions({ limit: 100 });
          setSubmissions(result.submissions || []);
      }
    } catch (err) {
      console.error('[LocationSubmissions] Error fetching submissions:', err);
      setLocalError(err.message || 'Error al cargar los envíos');
    }
  };

  const filteredSubmissions = submissions.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.location_name?.toLowerCase().includes(searchLower) ||
      item.location_city?.toLowerCase().includes(searchLower) ||
      item.submission_id?.toLowerCase().includes(searchLower) ||
      item.location_id?.toLowerCase().includes(searchLower) ||
      item.user_email?.toLowerCase().includes(searchLower)
    );
  });

  // Action handlers
  const handleOpenAction = (type, submission) => {
    setActionDialog({ isOpen: true, type, submission });
  };

  const handleOpenDelete = (location) => {
    setDeleteDialog({ isOpen: true, location });
  };

  const handleOpenCreateLocation = () => {
    setLocationFormModal({ isOpen: true, mode: 'create', location: null });
  };

  const handleOpenEditLocation = (location) => {
    setSelectedSubmission(null); // close detail modal if open
    setLocationFormModal({ isOpen: true, mode: 'edit', location });
  };

  const handleConfirmAction = async (notes) => {
    const { type, submission } = actionDialog;
    if (!submission) return;

    setProcessing(true);
    setLocalError('');

    try {
      let result;
      if (type === 'approve') {
        result = await approveSubmission(submission.submission_id);
        setSuccessMessage(`Ubicación "${submission.location_name}" aprobada. Location ID: ${result.location_id}`);
      } else if (type === 'duplicate') {
        result = await markSubmissionAsDuplicate(submission.submission_id, notes || null);
        setSuccessMessage(`Ubicación "${submission.location_name}" marcada como duplicada.`);
      } else {
        result = await rejectSubmission(submission.submission_id, notes || null);
        setSuccessMessage(`Ubicación "${submission.location_name}" rechazada.`);
      }

      await fetchSubmissions();
      setActionDialog({ isOpen: false, type: null, submission: null });
      setSelectedSubmission(null);
    } catch (err) {
      console.error('Error processing action:', err);
      setLocalError(err.message || 'Error al procesar la acción');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmDelete = async (notes) => {
    const { location } = deleteDialog;
    if (!location || !location.location_id) {
      setLocalError('Información de ubicación incompleta para eliminar');
      return;
    }

    setProcessing(true);
    setLocalError('');

    try {
      if (location.submission_id) {
        // Coupled delete: removes location AND rejects the submission
        await deleteLocationAndReject(location.location_id, location.submission_id, notes);
        setSuccessMessage(`Ubicación "${location.location_name}" eliminada y envío rechazado.`);
      } else {
        // Standalone delete: admin-created location, no submission to reject
        await deleteLocation(location.location_id);
        setSuccessMessage(`Ubicación "${location.location_name}" eliminada exitosamente.`);
      }

      await fetchSubmissions();
      setDeleteDialog({ isOpen: false, location: null });
      setSelectedSubmission(null);
    } catch (err) {
      console.error('Error deleting location:', err);
      setLocalError(err.message || 'Error al eliminar la ubicación');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmLocationForm = async (payload) => {
    setProcessing(true);
    setLocalError('');

    try {
      if (locationFormModal.mode === 'create') {
        const result = await createLocation(payload);
        setSuccessMessage(`Ubicación "${result.location_name}" creada. ID: ${result.location_id}`);
      } else {
        const targetId = locationFormModal.location.location_id;
        const result = await updateLocation(targetId, payload);
        setSuccessMessage(`Ubicación "${result.location_name}" actualizada.`);
      }

      await fetchSubmissions();
      setLocationFormModal({ isOpen: false, mode: 'create', location: null });
    } catch (err) {
      console.error('Error saving location:', err);
      setLocalError(err.message || 'Error al guardar la ubicación');
    } finally {
      setProcessing(false);
    }
  };

  const handleRefresh = () => fetchSubmissions();

  const StatusBadge = ({ status }) => {
    const styles = {
      Pending: 'bg-amber-50 text-amber-700 border-amber-100',
      Approved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      Rejected: 'bg-red-50 text-red-700 border-red-100',
      Duplicate: 'bg-slate-50 text-slate-700 border-slate-100'
    };
    const icons = { Pending: Clock, Approved: CheckCircle, Rejected: XCircle, Duplicate: Copy };
    const Icon = icons[status] || Clock;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.Pending}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status === 'Pending' ? 'Pendiente' :
          status === 'Approved' ? 'Aprobado' :
            status === 'Rejected' ? 'Rechazado' :
              status === 'Duplicate' ? 'Duplicado' : status}
      </span>
    );
  };

  const displayError = localError || contextError;

  // Helper: can this row be edited as a location? (must exist in prlocal_locations)
  const isEditableLocation = (item) =>
    !!item.location_id && (item.isFromLocationsTable || item.submission_status === 'Approved');

  return (
    <div className="min-h-screen bg-slate-50/50">
      <SuccessToast message={successMessage} onDismiss={() => setSuccessMessage('')} />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Envíos de Ubicaciones</h1>
              <p className="text-sm text-slate-500">Revisión y administración de lugares naturales.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* NEW: Add Location CTA */}
              <button
                onClick={handleOpenCreateLocation}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-bold hover:bg-violet-700 shadow-md shadow-violet-200 transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Agregar Ubicación
              </button>

              <button
                onClick={handleRefresh}
                disabled={contextLoading}
                className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50"
                title="Actualizar"
              >
                <RefreshCw className={`h-5 w-5 ${contextLoading ? 'animate-spin' : ''}`} />
              </button>

              <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg overflow-x-auto max-w-[300px] md:max-w-none">
                {['Pending', 'Approved', 'Rejected', 'Duplicate', 'AddedLocations', 'All'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    disabled={contextLoading}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap disabled:opacity-50 ${filterStatus === status
                        ? 'bg-white text-violet-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                      }`}
                  >
                    {status === 'All' ? 'Todos' :
                      status === 'Pending' ? 'Pendientes' :
                        status === 'Approved' ? 'Aprobados' :
                          status === 'Rejected' ? 'Rechazados' :
                            status === 'Duplicate' ? 'Duplicados' :
                              'Ubicaciones Agregadas'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre de lugar, ciudad, email o ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
            />
          </div>
        </div>
      </div>

      <ErrorAlert message={displayError} onDismiss={() => { clearError(); setLocalError(''); }} />

      {/* List Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden min-h-[400px]">
          {contextLoading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="h-8 w-8 text-violet-500 animate-spin mb-2" />
              <p className="text-slate-400 text-sm">Cargando envíos...</p>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <div className="bg-slate-50 p-3 rounded-full mb-3">
                <MapPin className="h-6 w-6 text-slate-300" />
              </div>
              <p className="text-slate-500 font-medium">
                {searchTerm ? 'No se encontraron resultados.' : 'No hay envíos en esta categoría.'}
              </p>
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="mt-2 text-sm text-violet-600 hover:text-violet-700">
                  Limpiar búsqueda
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Lugar</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Categoría</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Usuario</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredSubmissions.map((item) => (
                    <tr
                      key={item.submission_id || item.location_id}
                      onClick={() => setSelectedSubmission(item)}
                      className="group hover:bg-violet-50/50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-slate-200 overflow-hidden flex-shrink-0 border border-slate-300">
                            {(item.image_url_1 || item.image_url) ? (
                              <img
                                src={item.image_url_1 || item.image_url}
                                alt=""
                                referrerPolicy="no-referrer"
                                className="h-full w-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-slate-400">
                                <ImageIcon className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800 flex items-center gap-2">
                              {item.location_name}
                              {item.hasLocationData && (
                                <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" title="Datos de ubicación cargados">
                                  <Database className="h-3 w-3 inline" />
                                </span>
                              )}
                              {/* Admin-created badge: in DB but no submission */}
                              {item.isFromLocationsTable && !item.submission_id && (
                                <span className="text-xs text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded flex items-center gap-0.5" title="Creada por administrador">
                                  <Sparkles className="h-3 w-3" /> Admin
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 flex items-center gap-1">
                              <MapPin className="h-3 w-3" /> {item.location_city || 'Sin ciudad'}
                            </div>
                            {item.location_id && (filterStatus === 'AddedLocations' || filterStatus === 'Approved') && (
                              <div className="text-xs text-emerald-600 font-mono mt-0.5">
                                ID: {item.location_id}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-700 bg-slate-100 px-2 py-1 rounded-md font-medium">
                          {item.location_category || item.location_type || 'Sin categoría'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {!item.isFromLocationsTable ? (
                          <>
                            <div className="text-sm text-slate-700 truncate max-w-[150px]" title={item.user_email}>
                              {item.user_email || 'Anónimo'}
                            </div>
                            {item.user_credentials && (
                              <div className="text-[10px] text-blue-600 flex items-center gap-0.5 font-medium mt-0.5">
                                <BadgeCheck className="h-3 w-3" /> Verificado
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-sm text-slate-700">{item.credentials || 'N/A'}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {item.submission_date || item.date_added
                          ? new Date(item.submission_date || item.date_added).toLocaleDateString('es-PR')
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        {item.submission_status ? (
                          <StatusBadge status={item.submission_status} />
                        ) : (
                          <span className="text-xs text-slate-500 italic">Ubicación DB</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            className="p-2 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-100 transition-colors"
                            title="Ver Detalles"
                            onClick={(e) => { e.stopPropagation(); setSelectedSubmission(item); }}
                          >
                            <Eye className="h-5 w-5" />
                          </button>

                          {item.submission_status === 'Pending' && (
                            <button
                              className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                              title="Aprobación Rápida"
                              onClick={(e) => { e.stopPropagation(); handleOpenAction('approve', item); }}
                            >
                              <CheckCircle className="h-5 w-5" />
                            </button>
                          )}

                          {/* Edit — promoted to a labeled pill so it's discoverable in the row */}
                          {isEditableLocation(item) && (
                            <button
                              className="px-2.5 py-1.5 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200 transition-colors flex items-center gap-1 text-xs font-bold"
                              title="Editar Ubicación"
                              onClick={(e) => { e.stopPropagation(); handleOpenEditLocation(item); }}
                            >
                              <Edit3 className="h-3.5 w-3.5" />
                              Editar
                            </button>
                          )}

                          {(filterStatus === 'AddedLocations' || (item.location_id && item.submission_status === 'Approved')) && (
                            <button
                              className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Eliminar Ubicación"
                              onClick={(e) => { e.stopPropagation(); handleOpenDelete(item); }}
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!contextLoading && filteredSubmissions.length > 0 && (
          <div className="mt-4 text-center text-sm text-slate-500">
            Mostrando {filteredSubmissions.length} {filteredSubmissions.length === 1 ? 'resultado' : 'resultados'}
            {searchTerm && ` para "${searchTerm}"`}
          </div>
        )}
      </div>

      {/* Modals */}
      <SubmissionDetailModal
        submission={selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
        onAction={handleOpenAction}
        onDelete={handleOpenDelete}
        onEdit={handleOpenEditLocation}
      />

      <ActionDialog
        isOpen={actionDialog.isOpen}
        type={actionDialog.type}
        loading={processing}
        onClose={() => setActionDialog({ isOpen: false, type: null, submission: null })}
        onConfirm={handleConfirmAction}
      />

      <DeleteLocationDialog
        isOpen={deleteDialog.isOpen}
        location={deleteDialog.location}
        loading={processing}
        onClose={() => setDeleteDialog({ isOpen: false, location: null })}
        onConfirm={handleConfirmDelete}
      />

      <LocationFormModal
        isOpen={locationFormModal.isOpen}
        mode={locationFormModal.mode}
        location={locationFormModal.location}
        loading={processing}
        onClose={() => setLocationFormModal({ isOpen: false, mode: 'create', location: null })}
        onSubmit={handleConfirmLocationForm}
      />
    </div>
  );
}